import {
  ClientPersonReferenceFilterStatusEnum,
  type ClientComment,
  type ClientPersonReference
} from '@DigitaleDoerfer/digitale-doerfer-api';
import { useCallback, useMemo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import { Menu, Text } from 'react-native-paper';

import {
  ArrowOutwardIcon,
  BlockIcon,
  DeleteIcon,
  EditIcon,
  FeedbackIcon,
  ForumIcon
} from '@/assets';
import { WEB_MAX_WIDTH } from '@/constants/web';
import { useDeleteComment } from '@/features/api/hooks/grapevine-comment-events/useDeleteComment';
import { useGetOrganizationById } from '@/features/api/hooks/grapevine-organization/useGetOrganizationById';
import { useBlockPerson } from '@/features/api/hooks/participant-person-block-events/useBlockPerson';
import { useUnblockPerson } from '@/features/api/hooks/participant-person-block-events/useUnblockPerson';
import { useAppState } from '@/features/authorization/provider/useAppState';
import { ChatsRoute } from '@/features/chats/navigation/chatsRoute';
import { useFlag } from '@/features/flag/providers/useFlag';
import { FlagEntityTypeEnum } from '@/features/flag/types/flagEntityType';
import { AttachmentList } from '@/features/media/components/AttachmentList';
import { OrganizationLogo } from '@/features/media/components/avatar/OrganizationLogo';
import { PersonAvatar } from '@/features/media/components/avatar/PersonAvatar';
import { MediaItemImage } from '@/features/media/components/MediaItemImage';
import { MediaItemSize } from '@/features/media/types/mediaItem';
import { usePopup } from '@/features/popup/provider/usePopup';
import { CommentLikeButton } from '@/features/posts/components/comments/CommentLikeButton';
import { EditedHint } from '@/features/posts/components/EditedHint';
import { LabelButton } from '@/features/posts/components/LabelButton';
import { VerifiedName } from '@/features/posts/components/VerifiedName';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { useAppNavigation } from '@/navigation/hooks/useAppNavigation';
import { AppRoute } from '@/navigation/types/navigation';
import { ExtendedMenu } from '@/shared/components/ExtendedMenu';
import { OrganizationAccessState } from '@/shared/components/organization/organizationAccess';
import { CONTENT_MANAGER_ROLES } from '@/shared/components/organization/roles';
import { useOrganizationAccessFor } from '@/shared/components/organization/useOrganizationAccessFor';
import { PastTime } from '@/shared/components/PastTime';
import { AutoLinkText } from '@/shared/components/text/AutoLinkText';
import { hasItems } from '@/shared/utils/array';
import { isWeb } from '@/shared/utils/platform';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';
import { dayjs } from '@/time';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    blocked: { fontStyle: 'italic' },
    deleted: { fontStyle: 'italic' },
    replyToText: { fontWeight: 'bold', color: theme.colors.primary },
    row: { flexDirection: 'row', gap: 8 },
    images: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },
    image: { maxHeight: 73, borderRadius: 12 },
    content: { flex: 1, gap: 8, paddingTop: 4 },
    author: { flex: 1 },
    authorName: { fontWeight: 'bold' },
    onBehalfOf: { color: theme.colors.onSurfaceVariant }
  });

type Props = {
  commentsAllowed: boolean;
  comment: ClientComment;
  onEdit: (comment: ClientComment) => void;
  onReply: (comment: ClientComment) => void;
};

export const CommentItem: FC<Props> = ({
  commentsAllowed,
  comment,
  onEdit,
  onReply
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { t } = useTranslation(LocalizationNameSpace.POSTS, {
    keyPrefix: 'components.commentItem'
  });

  const navigation = useAppNavigation();

  const { userId } = useAppState();
  const { flag } = useFlag();

  const postId = comment.postId;
  const person = comment.creator;
  const replyTo = comment.replyTo;

  const isOrganizationComment = !isEmptyOrNull(comment.organizationId);
  const { data: organization } = useGetOrganizationById(comment.organizationId);
  const orgAccess = useOrganizationAccessFor(organization, {
    roles: CONTENT_MANAGER_ROLES
  });
  const isCreator = comment.creator?.id === userId;
  const canManageComment =
    isCreator ||
    (isOrganizationComment && orgAccess === OrganizationAccessState.Granted);

  const getName = useCallback(
    (person?: ClientPersonReference) =>
      !person || person.deleted
        ? t('deletedPerson')
        : `${person.firstName} ${person.lastName}`,
    [t]
  );

  const blocked =
    person?.filterStatus === ClientPersonReferenceFilterStatusEnum.Blocked;
  const blockPerson = useBlockPerson();
  const unblockPerson = useUnblockPerson();

  const createdTime = dayjs(comment.created);

  const [showMenu, setShowMenu] = useState<boolean>(false);

  const { width } = useWindowDimensions();
  const minWidth = isWeb ? Math.min(width, WEB_MAX_WIDTH) : width;

  const imageCount = useMemo(() => {
    if (width < 600) return 3;
    else return minWidth < 1200 ? 4 : 5;
  }, [minWidth, width]);

  const imageWidth = useMemo(
    () => (minWidth - 124) / imageCount,
    [imageCount, minWidth]
  );
  const imageHeight = Math.min(73, (imageWidth * 3) / 2);

  const deleteComment = useDeleteComment(postId);
  const { showAlert } = usePopup();

  const handleDeleteComment = useCallback(() => {
    showAlert({
      dismissable: true,
      labels: t('alerts.commentDeletion', {
        returnObjects: true
      }),
      onConfirm: () => deleteComment.mutate(comment.id)
    });
  }, [comment, deleteComment, showAlert, t]);

  const handleOpenGallery = useCallback(
    (position = 0) => {
      if (!hasItems(comment.images)) return;
      navigation.navigate(AppRoute.Gallery, {
        position,
        mediaUrls: comment.images.map(({ urls }) => urls.original)
      });
    },
    [comment.images, navigation]
  );

  const content = useMemo(() => {
    if (blocked)
      return (
        <Text variant="bodyLarge" style={styles.blocked}>
          {t('personBlocked')}
        </Text>
      );

    if (comment.deleted || !person)
      return (
        <Text variant="bodyLarge" style={styles.deleted}>
          {t('deleted')}
        </Text>
      );

    return (
      <View style={{ gap: 16 }}>
        {hasItems(comment.images) && (
          <View style={styles.images}>
            {comment.images.map((image, index) => (
              <MediaItemImage
                key={image.id}
                style={[
                  styles.image,
                  { width: imageWidth, height: imageHeight }
                ]}
                mediaItem={image}
                mediaSize={MediaItemSize.SMALL}
                elevation={2}
                onPress={() => handleOpenGallery(index)}
              />
            ))}
          </View>
        )}
        {hasItems(comment.attachments) && (
          <AttachmentList
            style={{ backgroundColor: theme.colors.onSecondary }}
            attachments={comment.attachments.map((documentItem) => ({
              documentItem: documentItem,
              documentItemId: documentItem.id
            }))}
            actionIcon={ArrowOutwardIcon}
          />
        )}
        <Text variant="bodyLarge" style={{ flex: 1 }}>
          {replyTo && (
            <Text style={styles.replyToText}>{`@${getName(replyTo)} `}</Text>
          )}
          <AutoLinkText variant="bodyLarge" text={comment.text} />
        </Text>
        <EditedHint
          created={comment.created}
          lastModified={comment.lastModified}
          lastEditor={comment.lastEditor}
          t={t}
        />
      </View>
    );
  }, [
    blocked,
    comment.attachments,
    comment.deleted,
    comment.images,
    comment.lastEditor,
    comment.lastModified,
    comment.created,
    comment.text,
    getName,
    handleOpenGallery,
    imageHeight,
    imageWidth,
    person,
    replyTo,
    styles.blocked,
    styles.deleted,
    styles.image,
    styles.images,
    styles.replyToText,
    t,
    theme.colors.onSecondary
  ]);

  if (!person) return;

  return (
    <View style={styles.row}>
      {isOrganizationComment ? (
        <OrganizationLogo mediaItem={organization?.logoImage} size={48} />
      ) : (
        <PersonAvatar person={person} size={48} canOpenGallery />
      )}
      <View style={styles.content}>
        <View style={{ gap: 6, flexDirection: 'row' }}>
          <View style={styles.author}>
            <VerifiedName
              variant="titleSmall"
              textStyle={styles.authorName}
              verified={isOrganizationComment}
              name={
                isOrganizationComment
                  ? (organization?.name ?? getName(person))
                  : getName(person)
              }
            />
            {isOrganizationComment && (
              <Text variant="bodySmall" style={styles.onBehalfOf}>
                {getName(person)}
              </Text>
            )}
          </View>
          <Text variant="bodyMedium">
            <PastTime time={createdTime} />
          </Text>
        </View>
        {content}
        <View style={[styles.row, { alignItems: 'center', paddingTop: 4 }]}>
          {!comment.deleted && !blocked && (
            <CommentLikeButton
              commentId={comment.id}
              liked={comment.liked}
              likeCount={comment.likeCount}
            />
          )}

          {canManageComment && comment.deleted ? (
            <></>
          ) : (
            <ExtendedMenu
              visible={showMenu}
              onDismiss={() => setShowMenu(false)}
              anchor={
                // IconButton was leading to a different height than LabelButton
                <LabelButton
                  onPress={() => setShowMenu(true)}
                  labelStyle={{ fontWeight: 'bold' }}
                >
                  {'···'}
                </LabelButton>
              }
            >
              {canManageComment ? (
                <>
                  <Menu.Item
                    leadingIcon={({ size, color }) => (
                      <EditIcon fill={color} width={size} height={size} />
                    )}
                    title={t('edit')}
                    onPress={() => {
                      onEdit(comment);
                      setShowMenu(false);
                    }}
                  />
                  <Menu.Item
                    theme={{ colors: { onSurface: theme.colors.error } }}
                    leadingIcon={({ size }) => (
                      <DeleteIcon
                        fill={theme.colors.error}
                        width={size}
                        height={size}
                      />
                    )}
                    title={t('delete')}
                    onPress={() => {
                      handleDeleteComment();
                      setShowMenu(false);
                    }}
                  />
                </>
              ) : (
                <>
                  {person && !blocked && (
                    <Menu.Item
                      leadingIcon={({ size, color }) => (
                        <ForumIcon fill={color} width={size} height={size} />
                      )}
                      title={t('startChat')}
                      onPress={() => {
                        setShowMenu(false);
                        navigation.navigate(ChatsRoute.NewChatOnComment, {
                          commentId: comment.id
                        });
                      }}
                    />
                  )}
                  {person && !blocked && commentsAllowed && (
                    <Menu.Item
                      leadingIcon={({ size, color }) => (
                        <ForumIcon fill={color} width={size} height={size} />
                      )}
                      title={t('reply')}
                      onPress={() => {
                        onReply(comment);
                        setShowMenu(false);
                      }}
                    />
                  )}
                  <Menu.Item
                    leadingIcon={({ size, color }) => (
                      <FeedbackIcon fill={color} width={size} height={size} />
                    )}
                    title={t('report')}
                    onPress={() => {
                      flag(FlagEntityTypeEnum.Comment, comment.id);
                      setShowMenu(false);
                    }}
                  />
                  {person && !blocked && (
                    <Menu.Item
                      leadingIcon={({ size, color }) => (
                        <BlockIcon fill={color} width={size} height={size} />
                      )}
                      title={t('block')}
                      onPress={() => {
                        setShowMenu(false);
                        blockPerson.mutate(person.id);
                      }}
                    />
                  )}
                  {person && blocked && (
                    <Menu.Item
                      leadingIcon={({ size, color }) => (
                        <BlockIcon fill={color} width={size} height={size} />
                      )}
                      title={t('unblock')}
                      onPress={() => {
                        setShowMenu(false);
                        unblockPerson.mutate(person.id);
                      }}
                    />
                  )}
                </>
              )}
            </ExtendedMenu>
          )}
        </View>
      </View>
    </View>
  );
};
